import { Component } from "@theme/component";

let photoSwipeModules;

async function loadPhotoSwipeModules() {
  if (!photoSwipeModules) {
    photoSwipeModules = Promise.all([
      import("./photoswipe-lightbox-5.4.3.esm.js"),
      import("./photoswipe-5.4.3.esm.js"),
    ]).then(([lightboxModule, photoSwipeModule]) => ({
      PhotoSwipeLightbox: lightboxModule.default,
      PhotoSwipe: photoSwipeModule.default,
    }));
  }

  return photoSwipeModules;
}

/**
 * A custom element that initializes PhotoSwipe for the product gallery.
 *
 * @extends Component
 */
export class LightboxDialog extends Component {
  requiredRefs = ["gallery"];

  static #stylesInjected = false;

  connectedCallback() {
    super.connectedCallback();
    this.injectStyles();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.lightbox) {
      this.lightbox.destroy();
      this.lightbox = null;
    }
  }

  async initPhotoSwipe() {
    if (this.lightbox) return;

    const galleryElement = this.refs.gallery;
    if (!galleryElement) return;
    const { PhotoSwipeLightbox, PhotoSwipe } = await loadPhotoSwipeModules();

    const backgroundStyle = this.dataset.backgroundStyle || "black";
    const backgroundConfig = this.getBackgroundConfig(backgroundStyle);

    this.lightbox = new PhotoSwipeLightbox({
      gallery: galleryElement,
      children: "a",
      pswpModule: PhotoSwipe,
      bgOpacity: backgroundConfig.bgOpacity,
      mainClass: backgroundConfig.mainClass,
      showHideAnimationType: "zoom",
    });

    this.lightbox.init();
  }

  getBackgroundConfig(backgroundStyle) {
    switch (backgroundStyle) {
      case "white":
        return {
          bgOpacity: 1,
          mainClass: "pswp--product-lightbox-white",
        };
      case "transparent":
        return {
          bgOpacity: 0,
          mainClass: "pswp--product-lightbox-transparent",
        };
      case "black":
      default:
        return {
          bgOpacity: 0.9,
          mainClass: "pswp--product-lightbox-black",
        };
    }
  }

  injectStyles() {
    if (LightboxDialog.#stylesInjected) return;

    const style = document.createElement("style");
    style.textContent = `
      .pswp--product-lightbox-black .pswp__bg {
        background: #000 !important;
      }

      .pswp--product-lightbox-white {
        --pswp-bg: #fff;
        --pswp-placeholder-bg: #fff;
        --pswp-icon-color: #111;
        --pswp-counter-color: #111;
        --pswp-icon-color-secondary: #111;
      }

      .pswp--product-lightbox-white .pswp__bg {
        background: #fff !important;
      }

      .pswp--product-lightbox-white .pswp__counter,
      .pswp--product-lightbox-white .pswp__button,
      .pswp--product-lightbox-white .pswp__button:hover,
      .pswp--product-lightbox-white .pswp__button:focus {
        color: #111 !important;
      }

      .pswp--product-lightbox-white .pswp__icn {
        color: inherit !important;
      }

      .pswp--product-lightbox-transparent {
        --pswp-bg: transparent;
        --pswp-placeholder-bg: transparent;
        --pswp-icon-color: #111;
        --pswp-counter-color: #111;
        --pswp-icon-color-secondary: #111;
      }

      .pswp--product-lightbox-transparent .pswp__bg {
        background: transparent !important;
      }

      .pswp--product-lightbox-transparent .pswp__counter,
      .pswp--product-lightbox-transparent .pswp__button,
      .pswp--product-lightbox-transparent .pswp__button:hover,
      .pswp--product-lightbox-transparent .pswp__button:focus {
        color: #111 !important;
        text-shadow: 0 0 6px rgba(255, 255, 255, 0.9);
      }

      .pswp--product-lightbox-transparent .pswp__icn {
        color: inherit !important;
        filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.85));
      }
    `;

    document.head.appendChild(style);
    LightboxDialog.#stylesInjected = true;
  }

  /**
   * Opens the lightbox at the specified index.
   * This method is called by the theme's event system when a main image is clicked.
   *
   * @param {number} index - The index of the media to show.
   * @param {Event} [event] - The event that triggered the open.
   */
  open(index, event) {
    if (event) event.preventDefault();
    this.initPhotoSwipe().then(() => {
      if (this.lightbox) {
        this.lightbox.loadAndOpen(index);
      }
    });
  }
}

if (!customElements.get("lightbox-dialog")) {
  customElements.define("lightbox-dialog", LightboxDialog);
}
